"use client";

import { ChevronRight, LayoutDashboard, DollarSign, CreditCard, Calendar, FileText, ClipboardList, TrendingUp, Receipt } from "lucide-react";
import { cn } from "../../lib/utils";
import { useNavigate, useLocation } from "react-router-dom";

const reportsLinks = [
  { id: "dashboard", label: "Dashboard", path: "/aesthetics/reports", icon: LayoutDashboard, description: "Overview & insights" },
  { id: "z-report", label: "Z-Report", path: "/aesthetics/reports/z-report", icon: FileText, description: "End of day report" },
  { id: "x-report", label: "X-Report", path: "/aesthetics/reports/x-report", icon: ClipboardList, description: "Mid-day summary" },
  { id: "daily-sales", label: "Daily Sales", path: "/aesthetics/reports/daily-sales", icon: TrendingUp, description: "Sales by day" },
  { id: "sales", label: "Sales Report", path: "/aesthetics/reports/sales", icon: DollarSign, description: "Revenue analysis" },
  { id: "payments", label: "Payments", path: "/aesthetics/reports/payments", icon: CreditCard, description: "Payment methods" },
  { id: "appointments", label: "Appointments", path: "/aesthetics/reports/appointments", icon: Calendar, description: "Booking history" },
  { id: "cash-up", label: "Cash Up", path: "/aesthetics/reports/cash-up", icon: Receipt, description: "Cash reconciliation" },
];

export default function AestheticReportsSubSidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current path matches exactly or is the base reports path for dashboard
  const isActive = (path: string) => {
    if (path === "/aesthetics/reports") {
      return location.pathname === "/aesthetics/reports" || location.pathname === "/aesthetics/reports/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="relative w-full lg:w-64 lg:ml-2 lg:mt-3">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Sidebar Header */}
          <div className="bg-gradient-to-r from-[#885ABB] to-[#9D6FD1] px-4 py-3">
            <h3 className="text-sm font-semibold text-white tracking-wide">Reports</h3>
            <p className="text-xs text-white/70 mt-0.5">Analytics & insights</p>
          </div>
          
          {/* Sidebar Items */}
          <div className="py-2">
            {reportsLinks.map((item) => {
              const active = isActive(item.path);
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-200 group relative",
                    active
                      ? "bg-purple-50 text-[#885ABB]"
                      : "text-gray-600 hover:bg-gray-50 hover:text-[#885ABB]"
                  )}
                >
                  {/* Active Indicator */}
                  {active && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-[#885ABB] rounded-r-full" />
                  )}
                  
                  {/* Icon */}
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                    active 
                      ? "bg-[#885ABB] text-white" 
                      : "bg-gray-100 text-gray-500 group-hover:bg-purple-100 group-hover:text-[#885ABB]"
                  )}>
                    <IconComponent size={16} />
                  </div>
                  
                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <span className={cn(
                      "block text-sm font-medium truncate",
                      active ? "text-[#885ABB]" : "text-gray-700 group-hover:text-[#885ABB]"
                    )}>
                      {item.label}
                    </span>
                    <span className="block text-xs text-gray-400 truncate">
                      {item.description}
                    </span>
                  </div>
                  
                  {/* Arrow */}
                  <ChevronRight
                    className={cn(
                      "w-4 h-4 transition-all duration-200",
                      active
                        ? "opacity-100 text-[#885ABB]"
                        : "opacity-0 group-hover:opacity-100 text-gray-400"
                    )}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile Horizontal Tabs */}
      <div className="lg:hidden">
        <div className="flex flex-row overflow-x-auto w-full bg-white py-2 border-b scrollbar-hide relative">
          {reportsLinks.map((item) => {
            const active = isActive(item.path);
            const IconComponent = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-all duration-200",
                  active
                    ? "text-[#885ABB] border-b-2 border-[#885ABB]"
                    : "text-gray-600 hover:text-[#885ABB]"
                )}
              >
                <IconComponent size={16} />
                {item.label}
              </button>
            );
          })}
          
          {/* Right side fade for mobile scroll */}
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
        </div>
      </div>
    </div>
  );
}

